import JSZip from 'jszip';
import {
    addToRequest,
    emptyRequestTotals,
    requestBytes,
    type FileTreeLimits,
    type RequestTotals,
} from './fileTree';
import { inflateWithinBudget } from './gzip';

/**
 * Replaces every archive in an import selection with its contents. Expand the whole selection or
 * none of it; the cap counts expanded bytes plus envelope, re-checked after each entry.
 *
 * A member is decompressed under the budget the cap leaves, never decompressed and then measured:
 * compression ratios are unbounded, so a drop that fits the cap can carry a member that does not
 * fit memory.
 */

/** Recognised by extension on the leaf name; the path a drop prefixes is not part of the test. */
const ZIP_PATTERN = /\.zip$/i;
const GZIP_PATTERN = /\.gz$/i;

/** Nesting-depth cap that bounds decompression-bomb recursion. */
const MAX_ARCHIVE_DEPTH = 4;

/** Apple's resource-fork sidecars, which `ditto`/Finder zips carry as real entries. */
const MACOS_METADATA_DIR = '__MACOSX/';
const MACOS_RESOURCE_FORK_PREFIX = '._';

export type ArchiveExpansion =
    | { status: 'ok'; files: File[] }
    | { status: 'too-many-bytes'; limitBytes: number }
    | { status: 'too-deep'; limit: number }
    | { status: 'encrypted' }
    | { status: 'unreadable' };

export type ExpandOptions = {
    signal?: AbortSignal;
    /** Called with the running count of files produced so far, expansions and pass-throughs alike. */
    onProgress?: (produced: number) => void;
};

/** The leaf of a drop-relative name: `study/ct/a.dcm` is `a.dcm`. */
function leafOf(name: string): string {
    const cut = Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\'));
    return cut < 0 ? name : name.slice(cut + 1);
}

export function isArchive(file: File): boolean {
    const leaf = leafOf(file.name);
    return ZIP_PATTERN.test(leaf) || GZIP_PATTERN.test(leaf);
}

export function hasArchive(files: readonly File[]): boolean {
    return files.some(isArchive);
}

function sanitizeEntryPath(path: string): string {
    return path.replace(/^[a-zA-Z]:[\\/]+/, '').replace(/^[\\/]+/, '');
}

function isMacOsMetadata(path: string): boolean {
    return (
        path.startsWith(MACOS_METADATA_DIR) ||
        leafOf(path).startsWith(MACOS_RESOURCE_FORK_PREFIX)
    );
}

/** The name a `.gz` inflates to: `CT001.dcm.gz` becomes `CT001.dcm`. */
function inflatedName(name: string): string {
    return name.replace(GZIP_PATTERN, '');
}

type Accumulator = {
    files: File[];
    totals: RequestTotals;
};

type Failure = Exclude<ArchiveExpansion, { status: 'ok' }>;

/** Records one produced file and reports whether the selection is still within its caps. */
function admit(
    file: File,
    accumulator: Accumulator,
    limits: FileTreeLimits,
    options: ExpandOptions
): Failure | undefined {
    accumulator.files.push(file);
    addToRequest(accumulator.totals, file);
    options.onProgress?.(accumulator.files.length);

    if (requestBytes(accumulator.totals, limits) > limits.maxBytes) {
        return { status: 'too-many-bytes', limitBytes: limits.maxBytes };
    }
    return undefined;
}

/**
 * The most a member may decompress to and leave the selection admissible. Admitting any member
 * costs at least its own byte count, and a nested archive's bytes never reach the accumulator at
 * all, so the unspent budget bounds every member alike; `admit` remains the exact cap.
 */
function remainingBytes(
    accumulator: Accumulator,
    limits: FileTreeLimits
): number {
    return Math.max(
        limits.maxBytes - requestBytes(accumulator.totals, limits),
        0
    );
}

/** jszip's typings expose neither member; both have been on its runtime throughout 3.x. */
type ZipEntryInternals = {
    _data?: { uncompressedSize?: number };
    internalStream(type: 'uint8array'): JSZip.JSZipStreamHelper<Uint8Array>;
};

/**
 * Decompresses one zip entry, refusing it the moment its output passes `maxBytes`. The declared
 * uncompressed size is tested first because it costs nothing, but that header is whatever the
 * archive says it is, so the chunks are also tallied as they arrive and the stream paused on the
 * first one over budget.
 */
function readEntryWithinBudget(
    entry: JSZip.JSZipObject,
    maxBytes: number
): Promise<Uint8Array[] | 'over-budget'> {
    const internals = entry as unknown as ZipEntryInternals;
    const declared = internals._data?.uncompressedSize;
    if (typeof declared === 'number' && declared > maxBytes) {
        return Promise.resolve('over-budget');
    }

    return new Promise((resolve, reject) => {
        const chunks: Uint8Array[] = [];
        let produced = 0;
        const stream = internals.internalStream('uint8array');
        stream
            .on('data', (chunk) => {
                produced += chunk.length;
                if (produced > maxBytes) {
                    stream.pause();
                    resolve('over-budget');
                    return;
                }
                chunks.push(chunk);
            })
            .on('error', (error) => reject(error))
            .on('end', () => resolve(chunks))
            .resume();
    });
}

/** Expands one `.zip` depth-first; entries taken in sorted path order for a reproducible import. */
async function expandZip(
    file: File,
    depth: number,
    accumulator: Accumulator,
    limits: FileTreeLimits,
    options: ExpandOptions
): Promise<Failure | undefined> {
    let zip: JSZip;
    try {
        zip = await new JSZip().loadAsync(await file.arrayBuffer());
    } catch (error) {
        // The library's own message is never surfaced: entry names routinely carry PHI.
        return isEncryptionError(error)
            ? { status: 'encrypted' }
            : { status: 'unreadable' };
    }

    for (const path of Object.keys(zip.files).sort()) {
        if (options.signal?.aborted) {
            return { status: 'unreadable' };
        }

        const entry = zip.files[path];
        if (entry.dir || isMacOsMetadata(path)) {
            continue;
        }

        let chunks: Uint8Array[] | 'over-budget';
        try {
            chunks = await readEntryWithinBudget(
                entry,
                remainingBytes(accumulator, limits)
            );
        } catch (error) {
            return isEncryptionError(error)
                ? { status: 'encrypted' }
                : { status: 'unreadable' };
        }
        if (chunks === 'over-budget') {
            return { status: 'too-many-bytes', limitBytes: limits.maxBytes };
        }

        const failure = await admitOrExpand(
            new File(chunks, sanitizeEntryPath(path)),
            depth + 1,
            accumulator,
            limits,
            options
        );
        if (failure !== undefined) {
            return failure;
        }
    }

    return undefined;
}

/** Inflates one gzip member into the file it names. */
async function expandGzip(
    file: File,
    depth: number,
    accumulator: Accumulator,
    limits: FileTreeLimits,
    options: ExpandOptions
): Promise<Failure | undefined> {
    let inflated: ArrayBuffer | 'over-budget';
    try {
        inflated = inflateWithinBudget(
            await file.arrayBuffer(),
            remainingBytes(accumulator, limits)
        );
    } catch {
        return { status: 'unreadable' };
    }
    if (inflated === 'over-budget') {
        return { status: 'too-many-bytes', limitBytes: limits.maxBytes };
    }

    return admitOrExpand(
        new File([inflated], inflatedName(file.name)),
        depth + 1,
        accumulator,
        limits,
        options
    );
}

/** JSZip signals a password-protected archive by message only. */
function isEncryptionError(error: unknown): boolean {
    return error instanceof Error && /encrypted/i.test(error.message);
}

async function admitOrExpand(
    file: File,
    depth: number,
    accumulator: Accumulator,
    limits: FileTreeLimits,
    options: ExpandOptions
): Promise<Failure | undefined> {
    if (!isArchive(file)) {
        return admit(file, accumulator, limits, options);
    }
    if (depth > MAX_ARCHIVE_DEPTH) {
        return { status: 'too-deep', limit: MAX_ARCHIVE_DEPTH };
    }
    return ZIP_PATTERN.test(leafOf(file.name))
        ? expandZip(file, depth, accumulator, limits, options)
        : expandGzip(file, depth, accumulator, limits, options);
}

/** Replaces every archive in a selection with its contents; `ok` only if the whole selection fit. */
export async function expandArchives(
    files: readonly File[],
    limits: FileTreeLimits,
    options: ExpandOptions = {}
): Promise<ArchiveExpansion> {
    const accumulator: Accumulator = {
        files: [],
        totals: emptyRequestTotals(),
    };

    for (const file of files) {
        if (options.signal?.aborted) {
            return { status: 'unreadable' };
        }
        const failure = await admitOrExpand(
            file,
            0,
            accumulator,
            limits,
            options
        );
        if (failure !== undefined) {
            return failure;
        }
    }

    return { status: 'ok', files: accumulator.files };
}
